import React, { useEffect, useMemo, useState } from "react";
import { AdminSidebar } from "./AdminDashboardPage";
import AdminHeader from "./AdminHeader";
import api from "../../services/api";
import "../../css/AdminContactUs.css";
import { Table } from "../../components/Table";
import { Search, Eye, Trash2, X, Mail } from "lucide-react";
import {
  getContacts,
  deleteContact,
  updateContactStatus,
} from "../../services/contactService";
import { useToast } from "../../contexts/ToastContext";

const AdminContactUs = () => {
  const { confirm } = useToast();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeContact, setActiveContact] = useState(null);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get("/contact");
      const list = Array.isArray(response?.data) ? response.data : [];
      setContacts(list);
      setFilteredContacts(list);
    } catch (err) {
      console.log(err);
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    const value = search.toLowerCase().trim();
    if (!value) {
      setFilteredContacts(contacts);
      return;
    }
    const filtered = contacts.filter(
      (item) =>
        item.name?.toLowerCase().includes(value) ||
        item.email?.toLowerCase().includes(value) ||
        item.message?.toLowerCase().includes(value)
    );
    setFilteredContacts(filtered);
  };

  const updateStatus = async (id, status) => {
    try {
      await updateContactStatus(id, status);
      fetchContacts();
    } catch (err) {
      console.log(err);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this inquiry?',
      message: 'This contact message will be removed.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await deleteContact(id);
      fetchContacts();
    } catch (err) {
      console.log(err);
    }
  };

  const formatDateShort = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const rows = useMemo(
    () =>
      filteredContacts.map((item, index) => ({
        ...item,
        _serial: index + 1,
      })),
    [filteredContacts]
  );

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "_serial",
        id: "slno",
        disableSortBy: true,
      },
      {
        Header: "Customer",
        id: "customer",
        accessor: (c) => c.name,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="acu-customer-cell">
              <span className="acu-customer-name">{c.name || "Anonymous"}</span>
              <span className="acu-customer-sub">{c.email || "No email"}</span>
            </div>
          );
        },
      },
      {
        Header: "Message",
        id: "message",
        accessor: (c) => c.message,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="acu-message-cell" title={c.message}>
              {c.message}
            </div>
          );
        },
      },
      {
        Header: "Status",
        id: "status",
        accessor: (c) => c.status,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <select
              className={`acu-select ${
                c.status === "New" ? "acu-status-new" : "acu-status-read"
              }`}
              value={c.status || "New"}
              onChange={(e) => updateStatus(c._id, e.target.value)}
            >
              <option value="New">New</option>
              <option value="Read">Read</option>
            </select>
          );
        },
      },
      {
        Header: "Received On",
        id: "createdAt",
        accessor: (c) => (c.createdAt ? new Date(c.createdAt).getTime() : 0),
        Cell: ({ row }) => (
          <span className="acu-date-cell">
            {formatDateShort(row.original.createdAt)}
          </span>
        ),
      },
      {
        Header: "Actions",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="acu-actions-cell">
              <button
                type="button"
                className="acu-view-btn"
                onClick={() => setActiveContact(row.original)}
                title="View full inquiry"
              >
                <Eye size={13} /> View
              </button>
              <button
                type="button"
                className="acu-delete-btn"
                onClick={() => handleDelete(c._id)}
                title="Delete message"
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <AdminHeader title="Contact Inquiries" />

          <div className="admin-contact-page">
            <div className="acu-header">
              <div>
                <h1>Contact Inquiries</h1>
                <p className="acu-subtitle">Manage customer questions, messages, and contact requests</p>
              </div>

              <div className="acu-header-actions">
                <span className="acu-count-pill">
                  {filteredContacts.length} {filteredContacts.length === 1 ? "Inquiry" : "Inquiries"}
                </span>
              </div>
            </div>

            <form onSubmit={handleSearch} className="acu-search-form">
              <div className="acu-search-input-wrap">
                <Search size={16} className="acu-search-icon" />
                <input
                  type="text"
                  placeholder="Search name, email, or message..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="acu-search-input"
                />
              </div>
              <button type="submit" className="acu-search-btn">
                Search
              </button>
              {search && (
                <button
                  type="button"
                  className="acu-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setFilteredContacts(contacts);
                  }}
                >
                  Clear
                </button>
              )}
            </form>

            {loading ? (
              <div className="acu-loading">
                <div className="acu-spinner" />
                <span>Loading inquiries...</span>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="acu-empty">
                <Mail size={32} />
                <p>No contact inquiries found</p>
              </div>
            ) : (
              <Table columns={columns} data={rows} show />
            )}
          </div>
        </div>
      </div>

      {activeContact && (
        <div className="acu-modal" onClick={() => setActiveContact(null)}>
          <div className="acu-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="acu-modal-header">
              <h3>Customer Inquiry</h3>
              <button
                type="button"
                className="acu-modal-close"
                onClick={() => setActiveContact(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="acu-modal-body">
              <div className="acu-modal-field">
                <label>Customer Name</label>
                <div>{activeContact.name || "—"}</div>
              </div>
              <div className="acu-modal-field">
                <label>Email Address</label>
                <div>{activeContact.email || "—"}</div>
              </div>
              <div className="acu-modal-field">
                <label>Status</label>
                <div>
                  <span className={`acu-badge ${activeContact.status === "New" ? "acu-status-new" : "acu-status-read"}`}>
                    {activeContact.status || "New"}
                  </span>
                </div>
              </div>
              <div className="acu-modal-field">
                <label>Message Content</label>
                <div className="acu-modal-message">{activeContact.message || "—"}</div>
              </div>
            </div>

            <div className="acu-modal-footer">
              <button
                type="button"
                className="acu-modal-btn"
                onClick={() => setActiveContact(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminContactUs;
